'use client'

import { useRef, useState } from 'react'
import { Play, Volume2, VolumeX } from 'lucide-react'

import { MINIMAX_H3_CDN } from '@/content/marketing/minimax-h3-prompts/prompts'

/**
 * Everything H3 makes, in one masonry wall.
 *
 * This was two sections — ours and fal's — stacked one after the other, which
 * split a single question ("what does this model produce") across two scrolls
 * and made the page argue with itself about which set mattered. One wall,
 * ordered by impact, with each card saying where it came from.
 *
 * The distinction is still worth keeping per-card, for opposite reasons in each
 * direction. Ours carry the exact prompt, because a visitor about to write one
 * gets more from a prompt than from a caption, and because it makes the clip
 * checkable. fal's carry no prompt but cover the capability our prompts never
 * asked for — legible rendered type — which is H3's most cited difference and
 * not something you demonstrate by accident.
 *
 * fal's are rehosted rather than hotlinked, and credited in the section copy.
 * Three hotlinked vendor clips were removed from these model pages for being
 * neither ours nor stable; pointing <video> at someone else's CDN only moves
 * that breakage somewhere we cannot see it.
 */

export type H3Clip = {
  /** File stem under MINIMAX_H3_CDN. */
  file: string
  label: string
  /** Prompt for ours; a plain description for fal's. */
  body: string
  /** True when `body` is the literal prompt that produced the clip. */
  isPrompt: boolean
  source: 'ours' | 'fal'
  tag: string
  aspect: string
}

/**
 * Ordered by impact, not by provenance. The three that open the wall are the
 * ones that also sit beside the generate form, because they are the clearest
 * at a glance: each renders type a visitor can actually read.
 */
export const H3_CLIPS: H3Clip[] = [
  {
    file: 'off-landing-page',
    label: 'Product landing page',
    body: 'A shoe page animating itself — headline, body copy and a button, rendered legibly rather than smeared.',
    isPrompt: false,
    source: 'fal',
    tag: 'Text rendering',
    aspect: 'aspect-video',
  },
  {
    file: 'off-game-ui',
    label: 'Game equipment UI',
    body: 'A character screen with a working HUD: stat plates, a cursor, and a confirm button that reads as a button.',
    isPrompt: false,
    source: 'fal',
    tag: 'UI animation',
    aspect: 'aspect-video',
  },
  {
    file: 'off-title-sequence',
    label: 'Anime title sequence',
    body: 'A multi-panel credit sequence carrying both Latin and Japanese type through the cuts.',
    isPrompt: false,
    source: 'fal',
    tag: 'Typography',
    aspect: 'aspect-video',
  },
  {
    file: 'gen-c',
    label: 'Potter at the wheel',
    body: "A potter's hands shape a tall clay vessel on a spinning wheel, wet clay glistening, water running down the sides, warm workshop light from a side window.",
    isPrompt: true,
    source: 'ours',
    tag: 'Macro',
    aspect: 'aspect-video',
  },
  {
    file: 'off-eyewear',
    label: 'Eyewear campaign',
    body: 'A vertical fashion campaign cut — three looks, consistent lighting, shot for social placements.',
    isPrompt: false,
    source: 'fal',
    tag: 'Vertical',
    aspect: 'aspect-[9/16]',
  },
  {
    file: 'gen-b',
    label: 'Night market wok',
    body: 'A street food vendor flips sizzling noodles in a wok over roaring flame at a night market, sparks and steam rising, neon signage bokeh behind.',
    isPrompt: true,
    source: 'ours',
    tag: 'Vertical',
    aspect: 'aspect-[9/16]',
  },
  {
    file: 'gen-a',
    label: 'Lighthouse stair',
    body: 'A lighthouse keeper climbs a spiral iron staircase at dawn, lantern in hand, cold blue light falling through narrow windows onto wet stone.',
    isPrompt: true,
    source: 'ours',
    tag: 'Native audio',
    aspect: 'aspect-video',
  },
  {
    file: 'off-brand-film',
    label: 'Brand film',
    body: 'A binocular-masked brand spot with an on-building banner and margin annotations held steady in frame.',
    isPrompt: false,
    source: 'fal',
    tag: 'Brand',
    aspect: 'aspect-video',
  },
  {
    file: 'gen-e',
    label: 'Ridge descent',
    body: 'A cyclist crests a ridge at golden hour and freewheels down the far side, camera tracking alongside, dust catching low sun, valley opening beyond.',
    isPrompt: true,
    source: 'ours',
    tag: 'Vertical',
    aspect: 'aspect-[9/16]',
  },
  {
    file: 'off-short-drama',
    label: 'Vertical short drama',
    body: 'A confrontation scene in 9:16 with sustained performance across a full 15-second take.',
    isPrompt: false,
    source: 'fal',
    tag: 'Vertical',
    aspect: 'aspect-[9/16]',
  },
  {
    file: 'gen-f',
    label: 'Diner window',
    body: 'Rain hammers a diner window at night while a half-empty coffee cup steams on the counter, neon sign reflected and rippling in the glass.',
    isPrompt: true,
    source: 'ours',
    tag: 'Focus rack',
    aspect: 'aspect-video',
  },
  {
    file: 'gen-d',
    label: 'Claymation fox',
    body: 'A hand-built claymation fox trots across a moss-covered log in a miniature forest set, stop-motion cadence, tiny visible fingerprints in the clay.',
    isPrompt: true,
    source: 'ours',
    tag: 'Square',
    aspect: 'aspect-square',
  },
]

export function MinimaxH3Gallery() {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])
  const [soundOn, setSoundOn] = useState<number | null>(null)

  const enter = (i: number) => {
    const v = videoRefs.current[i]
    if (v) void v.play().catch(() => {})
  }

  const leave = (i: number) => {
    const v = videoRefs.current[i]
    if (!v) return
    v.pause()
    v.currentTime = 0
  }

  /** One audible clip at a time — twelve soundtracks at once is a bounce. */
  const toggleSound = (i: number) => {
    const next = soundOn === i ? null : i
    setSoundOn(next)
    videoRefs.current.forEach((v, idx) => {
      if (!v) return
      v.muted = idx !== next
      if (idx === next) void v.play().catch(() => {})
    })
  }

  return (
    <div className="mt-8 gap-5 sm:columns-2 lg:columns-3 [column-fill:_balance]">
      {H3_CLIPS.map((s, i) => (
        <div
          key={s.file}
          className="group border-border bg-card mb-5 inline-block w-full break-inside-avoid overflow-hidden rounded-2xl border shadow-sm transition-shadow hover:shadow-md"
          onMouseEnter={() => enter(i)}
          onMouseLeave={() => leave(i)}
        >
          <div className={`relative overflow-hidden bg-black ${s.aspect}`}>
            <video
              ref={el => {
                videoRefs.current[i] = el
              }}
              src={`${MINIMAX_H3_CDN}/${s.file}.mp4`}
              poster={`${MINIMAX_H3_CDN}/${s.file}.jpg`}
              muted
              loop
              playsInline
              preload="none"
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/15 transition-opacity group-hover:opacity-0">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white">
                <Play className="size-3.5" /> Hover to play
              </span>
            </div>
            <span className="absolute top-3 left-3 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-semibold tracking-wider text-white uppercase">
              {s.tag}
            </span>
            <button
              type="button"
              aria-label={soundOn === i ? 'Mute' : 'Unmute'}
              className="absolute right-3 bottom-3 rounded-full bg-black/55 p-1.5 text-white/90 transition-colors hover:bg-black/75"
              onClick={() => toggleSound(i)}
            >
              {soundOn === i ? (
                <Volume2 className="size-4" />
              ) : (
                <VolumeX className="size-4" />
              )}
            </button>
          </div>
          <div className="p-4">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-foreground text-sm font-semibold">
                {s.label}
              </h3>
              <span className="text-muted-foreground/70 shrink-0 text-[10px] tracking-wider uppercase">
                {s.source === 'ours' ? 'Our render' : 'fal guide'}
              </span>
            </div>
            <p className="text-muted-foreground mt-1 text-xs leading-5">
              {s.isPrompt ? `“${s.body}”` : s.body}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}
